import React from "react";
import { Card, CardHeader } from "../../UI/DesignStandards";
import { formatSquadInfo } from "../../UI/DesignStandards/Utilities/DateTime";
import { FaMapMarkerAlt } from "react-icons/fa";
import { squadSpotsLeft, squadSpotCap } from "../../../utility/signupOccupancy";
import {
  SQUAD_ONLINE,
  squadStatusLabel,
} from "../../../utility/signupAvailability";

const SquadSelectionCard = ({
  selectedEvent,
  availableSquads,
  selectedSquad,
  onSquadSelect,
  occupancyBySquad = {},
  signupSettings = {},
  availabilityOptions = {},
  getSquadStatus,
  alreadyEnteredOnSquad,
  error = null,
}) => {
  if (!selectedEvent) {
    return null;
  }

  return (
    <Card className="mb-8">
      <CardHeader
        title="Select Squad"
        subtitle={`Choose your preferred squad for ${selectedEvent.eventName}`}
      />
      <div className="p-4 sm:p-6">
        {availableSquads.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            No available squads for this event.
          </p>
        ) : (
          <div className="grid gap-4">
            {availableSquads.map((squad) => {
              const isSelected = selectedSquad?.id === squad.id;
              const occupied = occupancyBySquad[squad.id] || 0;
              const status = getSquadStatus
                ? getSquadStatus(squad)
                : SQUAD_ONLINE.open;
              const selectable =
                status === SQUAD_ONLINE.open ||
                status === SQUAD_ONLINE.waitlist;
              const alreadyEntered = Boolean(alreadyEnteredOnSquad?.(squad));
              const maxSpots = squadSpotCap(
                squad,
                signupSettings,
                selectedEvent,
                availabilityOptions
              );
              const spotsLeft = squadSpotsLeft(occupied, maxSpots);
              const statusCopy = squadStatusLabel(status);
              const schedule = formatSquadInfo(squad.squadDate, squad.squadTime);
              const title = squad.squadName || schedule;

              return (
                <div
                  key={squad.id}
                  className={`relative p-4 rounded-lg transition-all duration-200 ${
                    !selectable
                      ? "bg-brand-cream border border-brand-charcoal/10 cursor-not-allowed opacity-70"
                      : isSelected
                        ? "bg-brand-charcoal text-brand-cream shadow-sm cursor-pointer"
                        : "bg-white border border-gray-200 hover:border-brand-charcoal/20 hover:shadow-md cursor-pointer"
                  }`}
                  onClick={() => {
                    if (selectable) onSquadSelect(squad);
                  }}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`text-lg font-semibold mb-2 break-words ${
                          isSelected && selectable
                            ? "text-white"
                            : "text-gray-900"
                        }`}
                      >
                        {title}
                      </h3>

                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-6">
                        {title !== schedule ? (
                        <div
                          className={`text-sm ${
                            isSelected && selectable
                              ? "text-brand-mist"
                              : "text-brand-muted"
                          }`}
                        >
                          {schedule}
                        </div>
                        ) : null}

                        {squad.bowlingCenters?.centerName && (
                          <div
                            className={`flex items-center gap-2 text-sm ${
                              isSelected && selectable
                                ? "text-brand-mist"
                                : "text-brand-muted"
                            }`}
                          >
                            <FaMapMarkerAlt className="w-3 h-3" />
                            {squad.bowlingCenters.centerName}
                          </div>
                        )}
                      </div>

                      <p
                        className={`text-sm text-center mt-2 ${
                          isSelected && selectable
                            ? "text-brand-mist"
                            : "text-brand-muted"
                        }`}
                      >
                        {statusCopy ||
                          (spotsLeft !== null
                            ? `${spotsLeft} of ${maxSpots} spots left`
                            : null)}
                      </p>
                      {alreadyEntered ? (
                        <p
                          className={`text-sm text-center mt-2 ${
                            isSelected && selectable
                              ? "text-brand-mist"
                              : "text-brand-charcoal"
                          }`}
                        >
                          You already have an entry on this squad.
                        </p>
                      ) : null}
                    </div>

                    {isSelected && selectable && (
                      <div className="flex items-center justify-center w-6 h-6 bg-white bg-opacity-20 rounded-full">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {error ? (
          <p data-signup-error="" className="mt-4 text-sm text-red-600">
            {error}
          </p>
        ) : null}
      </div>
    </Card>
  );
};

export default SquadSelectionCard;
